/* Ported from the vhc prototype's kit (src/pages/AIGenerated/vhc/components): its copy
   carried the ARIA and the tone scale ours never had, and ours took raw colours as
   props, so the fuller version moved here and became the only one. */
import clsx from 'clsx';

import s from './ProgressBar.module.scss';

/**
 * How far along something is — a profile filled in, a budget spent, a vessel
 * described.
 *
 * Five of these had been hand-built (profile completeness, vessel completeness,
 * the budget bar, the finance list rows, the equipment scan) with three
 * different tracks between them, and three of those tracks were a hardcoded
 * white wash — a dark-theme value, so on the light theme the track was white on
 * white and the bar read as a floating fill with no scale behind it: 40% and
 * 90% looked like the same measurement.
 *
 * `tone` is the fill, and it is the only thing a caller should need to choose:
 *   progress  teal, moving toward a goal — the default
 *   spend     blue, a quantity accumulating rather than an achievement
 *   over      red, past the line it was measured against
 *
 * @alias progress, completion bar, percentage bar, meter
 */
export const ProgressBar = ({
  value,
  tone = 'progress',
  size = 'md',
  label,
  className,
}: {
  /** 0–100. Clamped, because a budget can be 120% spent and a bar cannot. */
  value: number;
  tone?: 'progress' | 'spend' | 'over';
  size?: 'sm' | 'md';
  /** Announced to assistive tech; the visible number is the caller's own. */
  label?: string;
  className?: string;
}) => {
  const pct = Math.max(0, Math.min(100, value));
  return (
    <div
      className={clsx(s.track, size === 'sm' && s.sm, className)}
      role="progressbar"
      aria-valuenow={Math.round(pct)}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-label={label}
    >
      <div className={clsx(s.fill, s[tone])} style={{ width: `${pct}%` }} />
    </div>
  );
};
